import type { ReactElement } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { Icon, IconButton } from '../index';

import type { useTheme } from './theme';
import type { makeStyles } from './loggerStyles';
import { stateAria } from "../a11y";

// The logger header's right-hand cluster (clock / save / minimize / close).
// Split out of LoggerHeader.tsx (file cap, INBOX-10); the parent still owns
// every decision and passes it down. #318: no bell (one bell, in the app), no
// "?" (the notes open from the program line on the left) and no share (it
// lives on the session summary).
type LoggerHeaderActionsProps = {
  postSaveSummary: { sessionId: string; onDone: () => void } | null;
  isLive: boolean;
  submitting: boolean;
  entriesLength: number;
  todayDraft: boolean;
  inSuperset: boolean;
  onMinimize?: () => void;
  onSave: () => void;
  onCancel: () => void;
  onStartRest?: () => void;
  styles: ReturnType<typeof makeStyles>;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

export function LoggerHeaderActions({
  postSaveSummary,
  isLive,
  submitting,
  entriesLength,
  todayDraft,
  inSuperset,
  onMinimize,
  onSave,
  onCancel,
  onStartRest,
  styles,
  theme,
  t,
}: LoggerHeaderActionsProps): ReactElement {
  return (
    <View style={styles.rightActions}>
      {/* Manual rest-start clock, a LOGGER affordance, shown only while
          logging live: hidden on a past-session edit (you're correcting
          history, not resting between sets; Antenore 2026-07-10 "cose
          proprie di un logger e non di un editor") and hidden in
          prescription, where no rest is being taken at all (PE-22 K0). */}
      {postSaveSummary === null && isLive && onStartRest ? (
        // ⚠️ Deliberately NOT <IconButton>, though it encodes exactly this
        // "lit" state. Migrated to it first, and the device said no: the
        // primitive's default variant is a BORDERED surface2 box, and every
        // other icon in this header is bare, so the clock came out as the one
        // boxed glyph in the row. A primitive that is right in the abstract
        // can still be wrong for the cluster it lands in.
        // The active tint below is IconButton's OWN token pair, not a new
        // one, so the two can't drift on colour. Migrating the whole header
        // cluster is the real fix and is registered in next/consolidation.md
        // : it is a header restyle, not a line of this slice.
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ selected: inSuperset }}
          {...stateAria({ selected: inSuperset }, "button")}
          accessibilityLabel={t('mobile.logger.startRestManual', {
            defaultValue: 'Start rest timer',
          })}
          onPress={onStartRest}
          hitSlop={8}
          style={({ pressed }) => [
            styles.headerIconButton,
            inSuperset && styles.headerIconButtonActive,
            pressed && styles.headerIconButtonPressed,
          ]}
        >
          <Icon
            name="clock"
            size={theme.typography.sizes.lg}
            color={theme.palette.ink}
          />
        </Pressable>
      ) : null}
      {postSaveSummary === null ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('mobile.logger.saveWorkout', {
            defaultValue: 'Save workout',
          })}
          onPress={onSave}
          disabled={submitting || entriesLength === 0 || todayDraft}
          hitSlop={8}
          style={({ pressed }) => [
            styles.headerIconButton,
            pressed && styles.headerIconButtonPressed,
            (submitting || entriesLength === 0 || todayDraft) &&
              styles.actionDisabled,
          ]}
        >
          {submitting ? (
            <ActivityIndicator color={theme.palette.brand500} />
          ) : (
            <Icon
              name="save"
              size={theme.typography.sizes.lg}
              color={theme.palette.brand500}
            />
          )}
        </Pressable>
      ) : null}
      {onMinimize && !postSaveSummary ? (
        <IconButton
          theme={theme}
          name="chevron-down"
          variant="bare"
          glyphTone="ink"
          accessibilityLabel={t('mobile.logger.minimize')}
          onPress={onMinimize}
          disabled={submitting}
        />
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          postSaveSummary
            ? t('mobile.postSession.done', { defaultValue: 'Done' })
            : t('mobile.logger.closeWorkout', {
                defaultValue: 'Close workout',
              })
        }
        onPress={postSaveSummary ? postSaveSummary.onDone : onCancel}
        disabled={submitting}
        hitSlop={8}
        style={({ pressed }) => [
          styles.headerIconButton,
          pressed && styles.headerIconButtonPressed,
          submitting && styles.actionDisabled,
        ]}
      >
        {/* headerIconGlyphDanger overrides headerIconGlyph color: p.ink → p.ink2 */}
        <Icon name="close" size={theme.typography.sizes.lg} color={theme.palette.ink2} />
      </Pressable>
    </View>
  );
}
